import React from "react";
import { Container, Row, Col } from "react-bootstrap";
import { Link } from "react-router-dom";
import {
  FaArrowRight,
  FaArrowUp,
  FaEnvelope,
  FaFacebookF,
  FaLinkedinIn,
  FaMapMarkerAlt,
  FaPhone,
  FaTwitter,
} from "react-icons/fa";

import { services } from "../data/services";
import { technologies } from "../data/technologies";

const scrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

// TODO: swap "#" for the real profile URLs once they exist. Kept as anchors
// rather than bare icons so they're focusable and screen-reader labelled.
const SOCIALS = [
  { label: "Sarvavya on LinkedIn", Icon: FaLinkedinIn, href: "#" },
  { label: "Sarvavya on Facebook", Icon: FaFacebookF, href: "#" },
  { label: "Sarvavya on Twitter", Icon: FaTwitter, href: "#" },
];

const SECTORS = [
  "Banking",
  "Payments",
  "Social Housing",
  "Education",
  "Retail & eCommerce",
];

function Footer() {
  return (
    <footer className="ftr">
      <style>{`
        .ftr {
          --peach-deep: #ff6b35;
          --peach: #ff9066;
          --peach-soft: #f3c09f;

          position: relative;
          /* flow-root establishes a BFC. Without it the CTA panel's negative
             margin collapses through and shifts the whole footer up instead
             of lifting the panel out onto the section above. */
          display: flow-root;
          padding-top: 0;
          color: #fff;
          background:
            radial-gradient(48rem 26rem at 88% 0%, rgba(255,107,53,.20), transparent 62%),
            linear-gradient(135deg, #0f172a 0%, #1e293b 55%, #0b1120 100%);
        }
        /* Peach hairline replaces the old amber 4px slab */
        .ftr::before {
          content: ""; position: absolute; top: 0; left: 0; right: 0; height: 3px;
          background: linear-gradient(90deg, var(--peach-deep), var(--peach-soft), var(--peach-deep));
        }

        /* --- Overlapping CTA panel --- */
        .ftr__ctaWrap { position: relative; z-index: 2; padding-top: 44px; }
        @media (min-width: 768px) {
          .ftr__ctaWrap { margin-top: -84px; padding-top: 0; }
        }
        .ftr__cta {
          display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between;
          gap: 20px; padding: clamp(24px, 3.4vw, 38px);
          border-radius: 24px;
          background: linear-gradient(135deg, var(--peach-deep), var(--peach) 60%, var(--peach-soft));
          box-shadow: 0 26px 55px rgba(255,107,53,.34);
          /* Navy on peach is 8:1; white would be 2.8:1 and fail AA */
          color: #0f172a;
        }
        .ftr__ctaTitle {
          margin: 0; font-weight: 800; letter-spacing: -.02em;
          font-size: clamp(1.3rem, 2.6vw, 2rem); line-height: 1.2;
        }
        .ftr__ctaText { margin: 6px 0 0; color: #3d1f10; font-size: .96rem; }
        .ftr__ctaBtn {
          display: inline-flex; align-items: center; gap: 10px;
          background: #0f172a; color: #fff; text-decoration: none;
          font-weight: 700; padding: 14px 26px; border-radius: 14px;
          white-space: nowrap;
          transition: transform .22s ease, box-shadow .22s ease, background .22s ease;
        }
        .ftr__ctaBtn:hover, .ftr__ctaBtn:focus-visible {
          background: #1e293b; color: #fff;
          transform: translateY(-3px); box-shadow: 0 14px 30px rgba(15,23,42,.4);
        }
        .ftr__ctaBtn svg { transition: transform .22s ease; }
        .ftr__ctaBtn:hover svg { transform: translateX(5px); }

        /* --- Body --- */
        .ftr__body { padding: clamp(46px, 6vw, 72px) 0 0; }
        .ftr__brand {
          font-weight: 800; letter-spacing: 1px; font-size: 1.6rem; color: #fff;
        }
        .ftr__brand span {
          background: linear-gradient(105deg, var(--peach-soft), var(--peach));
          -webkit-background-clip: text; background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        .ftr__tag { color: #cbd5e1; line-height: 1.8; margin-top: 12px; }
        .ftr__sub { color: #94a3b8; font-size: .93rem; }

        .ftr__socials { display: flex; gap: 10px; margin-top: 18px; }
        .ftr__social {
          width: 40px; height: 40px; border-radius: 12px;
          display: grid; place-items: center;
          color: var(--peach-soft);
          background: rgba(255,144,102,.12);
          border: 1px solid rgba(255,144,102,.28);
          transition: background .24s ease, color .24s ease,
                      transform .24s ease, border-color .24s ease;
        }
        .ftr__social:hover, .ftr__social:focus-visible {
          background: linear-gradient(135deg, var(--peach-deep), var(--peach));
          border-color: transparent; color: #fff; transform: translateY(-3px);
        }

        /* Column headings get a short peach underline */
        .ftr__colHead {
          position: relative; display: inline-block;
          font-weight: 700; font-size: 1.02rem; color: #fff;
          margin-bottom: 20px; padding-bottom: 10px;
        }
        .ftr__colHead::after {
          content: ""; position: absolute; left: 0; bottom: 0;
          width: 30px; height: 3px; border-radius: 999px;
          background: linear-gradient(90deg, var(--peach-deep), var(--peach-soft));
        }

        .ftr__list { list-style: none; margin: 0; padding: 0; }
        .ftr__list li { margin-bottom: 9px; }
        /* Beat theme.css's amber .footer-link/.footer-heading hovers */
        .ftr .footer-link { color: #b9c4d4; font-size: .94rem; }
        .ftr .footer-link:hover, .ftr .footer-link:focus-visible {
          color: var(--peach); padding-left: 5px;
        }
        .ftr .footer-heading:hover h2,
        .ftr .footer-heading:hover .ftr__colHead { color: var(--peach); }

        .ftr__contact {
          display: flex; align-items: flex-start; gap: 11px;
          color: #cbd5e1; margin-bottom: 13px;
          text-decoration: none; font-size: .94rem;
        }
        a.ftr__contact:hover { color: var(--peach); }
        .ftr__contactIcon {
          width: 32px; height: 32px; border-radius: 10px; flex-shrink: 0;
          display: grid; place-items: center; font-size: .8rem;
          background: rgba(255,144,102,.12); color: var(--peach);
        }
        .ftr__chips { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 4px; }
        .ftr__chip {
          background: rgba(255,255,255,.07); color: #b9c4d4;
          border-radius: 999px; padding: 4px 11px; font-size: .72rem;
        }

        /* --- Bottom bar --- */
        .ftr__bar {
          margin-top: clamp(34px, 4vw, 52px);
          border-top: 1px solid rgba(255,255,255,.12);
          padding: 20px 0 24px;
          display: flex; flex-wrap: wrap; gap: 14px;
          align-items: center; justify-content: space-between;
          color: #94a3b8; font-size: .88rem;
        }
        .ftr__top {
          display: inline-flex; align-items: center; gap: 9px;
          background: rgba(255,144,102,.12); color: var(--peach-soft);
          border: 1px solid rgba(255,144,102,.28);
          border-radius: 999px; padding: 9px 18px;
          font-size: .84rem; font-weight: 600; cursor: pointer;
          transition: background .24s ease, color .24s ease, transform .24s ease;
        }
        .ftr__top:hover, .ftr__top:focus-visible {
          background: linear-gradient(135deg, var(--peach-deep), var(--peach));
          color: #0f172a; transform: translateY(-2px);
        }
        @media (max-width: 575.98px) {
          .ftr__bar { justify-content: center; text-align: center; }
        }

        @media (prefers-reduced-motion: reduce) {
          .ftr__ctaBtn, .ftr__social, .ftr__top, .ftr__ctaBtn svg { transition: none; }
          .ftr__ctaBtn:hover, .ftr__social:hover, .ftr__top:hover { transform: none; }
        }
      `}</style>

      <Container>
        {/* CTA panel — rides up onto the section above from md */}
        <div className="ftr__ctaWrap">
          <div className="ftr__cta">
            <div>
              <h2 className="ftr__ctaTitle">Ready to start your next programme?</h2>
              <p className="ftr__ctaText">
                Tell us the outcome you need — we'll shape the delivery around it.
              </p>
            </div>
            <Link to="/contact" onClick={scrollTop} className="ftr__ctaBtn">
              Schedule a Consultation
              <FaArrowRight />
            </Link>
          </div>
        </div>

        <div className="ftr__body">
          <Row className="gy-4">
            {/* Company */}
            <Col lg={3} md={6}>
              <Link to="/" onClick={scrollTop} className="text-decoration-none footer-heading">
                <h2 className="ftr__brand">
                  SARVAV<span>YA</span>
                </h2>
              </Link>

              <p className="ftr__tag">
                Your Partner for Business Transformation, Technology Delivery &amp;
                Digital Growth.
              </p>
              <p className="ftr__sub">
                Delivering Technology, Transformation &amp; Digital Excellence.
              </p>

              {/* Real anchors, not bare icons — focusable and labelled */}
              <div className="ftr__socials">
                {SOCIALS.map(({ label, Icon, href }) => (
                  <a
                    key={label}
                    href={href}
                    aria-label={label}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ftr__social"
                  >
                    <Icon />
                  </a>
                ))}
              </div>
            </Col>

            {/* Services */}
            <Col lg={3} md={6}>
              <Link
                to="/services"
                onClick={scrollTop}
                className="text-decoration-none footer-heading"
              >
                <h3 className="ftr__colHead">Services</h3>
              </Link>
              <ul className="ftr__list">
                {services.map((service) => (
                  <li key={service.slug}>
                    <Link
                      to={`/services/${service.slug}`}
                      onClick={scrollTop}
                      className="footer-link"
                    >
                      {service.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </Col>

            {/* Technology */}
            <Col lg={3} md={6}>
              <Link
                to="/technology"
                onClick={scrollTop}
                className="text-decoration-none footer-heading"
              >
                <h3 className="ftr__colHead">Technology Expertise</h3>
              </Link>
              <ul className="ftr__list">
                {technologies.map((tech) => (
                  <li key={tech.slug}>
                    <Link
                      to={`/technology/${tech.slug}`}
                      onClick={scrollTop}
                      className="footer-link"
                    >
                      {tech.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </Col>

            {/* Contact */}
            <Col lg={3} md={6}>
              <Link
                to="/contact"
                onClick={scrollTop}
                className="text-decoration-none footer-heading"
              >
                <h3 className="ftr__colHead">Contact</h3>
              </Link>

              {/* Actionable on mobile, where tapping a number should dial */}
              <a href="mailto:info@sarvavya.com" className="ftr__contact">
                <span className="ftr__contactIcon">
                  <FaEnvelope />
                </span>
                info@sarvavya.com
              </a>

              <a href="tel:+44XXXXXXXXX" className="ftr__contact">
                <span className="ftr__contactIcon">
                  <FaPhone />
                </span>
                +44 XXX XXX XXXX
              </a>

              <span className="ftr__contact">
                <span className="ftr__contactIcon">
                  <FaMapMarkerAlt />
                </span>
                United Kingdom
              </span>

              <div className="ftr__chips">
                {SECTORS.map((s) => (
                  <span className="ftr__chip" key={s}>
                    {s}
                  </span>
                ))}
              </div>
            </Col>
          </Row>

          <div className="ftr__bar">
            {/* Derived, not hardcoded — the old "2026" would silently go stale */}
            <span>
              © {new Date().getFullYear()} Sarvavya Consultancy. All Rights Reserved.
            </span>

            <button type="button" onClick={scrollTop} className="ftr__top">
              Back to top
              <FaArrowUp />
            </button>
          </div>
        </div>
      </Container>
    </footer>
  );
}

export default Footer;
